import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { useFormatters } from '@shared/lib/i18n/formatters';
import { cn } from '@shared/lib/cn';
import { useSession } from '@shared/session/store';
import { Button, EmptyState, IconButton, QueryState, Skeleton } from '@shared/ui';
import { BELL_POLL_MS, notificationKeys, notificationsApi, type NotificationView } from '../api';

/** In-app deep links are app paths; anything else is ignored (never navigate off-site). */
export function safeDeepLink(link: string | null | undefined): string | null {
  return link && link.startsWith('/app') && !link.startsWith('//') ? link : null;
}

export function NotificationItem({ n, onOpen, compact = false, tabIndex }: { n: NotificationView; onOpen: (n: NotificationView) => void; compact?: boolean; tabIndex?: number }) {
  const f = useFormatters();
  const unread = !n.read_at;
  return (
    <button
      type="button"
      tabIndex={tabIndex}
      onClick={() => onOpen(n)}
      className={cn(
        'flex w-full items-start gap-3 rounded-md px-3 py-2 text-start hover:bg-surface-tint focus-visible:focus-ring',
        unread && 'bg-primary-subtle/40',
      )}
    >
      <span aria-hidden="true" className={cn('mt-2 size-2 shrink-0 rounded-full', unread ? 'bg-primary' : 'bg-transparent')} />
      <span className="min-w-0 flex-1">
        <span className={cn('block text-sm text-primary', unread ? 'font-semibold' : 'font-medium', compact && 'truncate')}>
          <bdi dir="auto">{n.title}</bdi>
        </span>
        {n.body ? (
          <span className={cn('block text-sm text-secondary', compact && 'line-clamp-2')}>
            <bdi dir="auto">{n.body}</bdi>
          </span>
        ) : null}
        <time dateTime={n.created_at} className="block text-xs text-muted">
          {f.date(n.created_at, 'dateTimeMedium')}
        </time>
      </span>
    </button>
  );
}

export function useOpenNotification() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return (n: NotificationView) => {
    if (!n.read_at) {
      void notificationsApi
        .read(n.id)
        .then(() => queryClient.invalidateQueries({ queryKey: notificationKeys.all }))
        .catch(() => undefined);
    }
    const to = safeDeepLink(n.deep_link);
    if (to) void navigate({ to });
  };
}

/** Top-bar bell: unread badge (polled every 30 s) and the latest 10 in a dropdown. */
export function NotificationBell() {
  const { t } = useTranslation('notifications');
  const f = useFormatters();
  const orgId = useSession((s) => s.activeOrganizationId);
  const queryClient = useQueryClient();
  const count = useQuery({
    queryKey: notificationKeys.count(orgId),
    queryFn: notificationsApi.unreadCount,
    refetchInterval: BELL_POLL_MS,
    retry: false,
  });
  const latest = useQuery({ queryKey: notificationKeys.latest(orgId), queryFn: () => notificationsApi.list({ limit: 10 }), enabled: false });
  const readAll = useMutation({
    mutationFn: notificationsApi.readAll,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
  const open = useOpenNotification();
  const unread = count.data?.count ?? 0;
  const label = unread > 0 ? t('bell.labelUnread', { count: unread }) : t('bell.label');

  return (
    <RadixMenu.Root onOpenChange={(o) => o && void latest.refetch()}>
      {/* The button itself is the trigger (aria-haspopup/expanded on the focusable element); the badge sits beside it. */}
      <span className="relative inline-flex">
        <RadixMenu.Trigger asChild>
          <IconButton icon={Bell} label={label} showTooltip={false} />
        </RadixMenu.Trigger>
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -end-0.5 -top-0.5 min-w-[1.125rem] rounded-full bg-danger px-1 text-center text-[0.65rem] font-semibold leading-[1.125rem] text-on-danger"
            >
              {unread > 99 ? '99+' : f.number(unread)}
            </span>
          ) : null}
      </span>
      <RadixMenu.Portal>
        <RadixMenu.Content
          align="end"
          sideOffset={8}
          collisionPadding={8}
          aria-label={t('bell.title')}
          className="z-dropdown flex w-[min(24rem,calc(100vw-2rem))] flex-col rounded-lg border border-border-subtle bg-surface-raised shadow-2"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
            <h2 className="text-sm font-semibold text-heading">{t('bell.title')}</h2>
            <RadixMenu.Item asChild disabled={unread === 0} onSelect={(e) => { e.preventDefault(); readAll.mutate(); }}>
              <Button variant="ghost" size="sm" leadingIcon={CheckCheck} disabled={unread === 0} loading={readAll.isPending}>
                {t('actions.readAll')}
              </Button>
            </RadixMenu.Item>
          </div>
          <div className="max-h-[60dvh] overflow-y-auto p-1">
            <QueryState
              query={latest}
              loading={<Skeleton lines={3} className="p-3" />}
              isEmpty={(d) => d.data.length === 0}
              empty={<EmptyState icon={Bell} title={t('empty.title')} description={t('empty.body')} as="h3" className="py-6" />}
            >
              {(d) => (
                <ul className="flex flex-col">
                  {d.data.map((n) => (
                    <li key={n.id}>
                      <RadixMenu.Item asChild onSelect={() => open(n)}>
                        <div className="rounded-md outline-none data-[highlighted]:bg-surface-tint">
                          <NotificationItem n={n} onOpen={() => undefined} compact tabIndex={-1} />
                        </div>
                      </RadixMenu.Item>
                    </li>
                  ))}
                </ul>
              )}
            </QueryState>
          </div>
          <div className="border-t border-border-subtle p-2 text-center">
            <RadixMenu.Item asChild>
              <Link to="/app/notifications" className="link text-sm outline-none data-[highlighted]:underline">
                {t('bell.viewAll')}
              </Link>
            </RadixMenu.Item>
          </div>
        </RadixMenu.Content>
      </RadixMenu.Portal>
    </RadixMenu.Root>
  );
}
